import { FeeStatus, StudyLevel } from '../enums';
import { ApplyRequirementConfig } from '../offer';
import { z } from 'zod';

interface AcademicRequirement {
  name: string;
  academicRequirementId?: string; // Used later for the equivalency lookup
}

interface LanguageRequirement {
  name: string;
  languageRequirementId?: string; // Used later for the equivalency lookup
}

// TODO: This needs converting to not have p_key etc at this level so it's a normal CasShieldDBItem where this is data
export interface IApplyCourse {
  s_key: string;
  id: string;
  uni: string;
  shortId: string;
  intakeId: string;
  mode: 'Full-time' | 'Part-time'; // TODO: Missing sandwitch etc
  externalId: string;

  name: string;
  shortName: string;
  award: string;
  rqfLevel: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'; // TODO: Should these just be numbers?
  level: StudyLevel;
  awardingBody?: string;

  duration: {
    min: number;
    max: number;
  };
  entryPoints: Array<'Year 1' | 'Year 2' | 'Year 3' | 'Year 4' | 'Year 5' | 'Year 6'>; // TODO: Should entryPoints be an array or single value? Should these just be numbers?

  category: string;
  campus: string;
  faculty: string;
  department?: string;

  isHTQ: boolean;
  UCASApplyCode?: string;
  UCASFacultyCode?: string;

  availableHome: boolean; // TODO: These should probably be Array<FeeStatus> to make it easier to match
  availableOverseas: boolean; // TODO: These should probably be Array<FeeStatus> to make it easier to match

  feeHome?: number;
  feeOverseas?: number;

  startMonthHome?: string;
  startDateHome?: string;
  endDateHome?: string;
  startMonthOverseas?: string;
  startDateOverseas?: string;
  endDateOverseas?: string;

  lastAcceptanceDate?: string;

  hecos?: Array<{
    code: string; // TODO: This should be an enum or something
    name: string;
    percentage: number;
  }>;

  // TODO: Settings have just be placed here for now, it needs more thought. The requirements must match in ApplyRegion
  academicRequirements?: {
    [FeeStatus.Home]: AcademicRequirement | undefined; // If undefined it is turned off no matter what is in requirements
    [FeeStatus.Overseas]: AcademicRequirement | undefined; // If undefined it is turned off no matter what is in requirements
  };
  languageRequirements?: {
    [FeeStatus.Home]: LanguageRequirement | undefined; // If undefined it is turned off no matter what is in requirements
    [FeeStatus.Overseas]: LanguageRequirement | undefined; // If undefined it is turned off no matter what is in requirements
  };
  requirements: ApplyRequirementConfig;
}

export type ApplyCourse = z.infer<typeof ApplyCourseSchema>;

export type NewApplyCourse = Omit<ApplyCourse, 'p_key' | 's_key'>;

const noSpecialChars = /^[a-zA-Z0-9_ ()-]*$/;
const safeString = z.string().regex(noSpecialChars, {
  message: 'Only letters, numbers, _, -, / (,) and spaces are allowed'
});

export const ApplyCourseSchema = z
  .object({
    p_key: safeString.optional(),
    s_key: safeString.optional(),

    username: z.string().optional(),
    lastModified: z.string().optional(),

    shortId: safeString.optional(),
    name: safeString,
    shortName: safeString,

    availableHome: z.boolean(),
    feeHome: z.number().nonnegative(),
    availableOverseas: z.boolean(),
    feeOverseas: z.number().nonnegative(),

    mode: safeString,
    startMonthHome: z.string(),
    duration: z
      .object({
        min: z.number().nonnegative(),
        max: z.number().nonnegative()
      })
      .refine(
        ({ min, max }) => {
          return min <= max;
        },
        {
          message: 'Duration minimum must be lesser than duration maximum'
        }
      ),
    startDateHome: z.string().date(),
    endDateHome: z.string().date(),
    lastAcceptanceDate: z.string().date(),

    award: safeString,
    entryPoints: z.array(safeString),
    campus: z.array(safeString),

    department: safeString,
    faculty: safeString,
    category: safeString,
    isHTQ: z.boolean(),

    hecos: z.array(z.object({ code: safeString, percentage: z.number() })),
    abbreviatedTitle: safeString,
    UCASApplyCode: safeString,
    UCASFacultyCode: safeString
  })
  .refine(
    props => {
      return Date.parse(props.startDateHome || '') <= Date.parse(props.endDateHome || '');
    },
    { path: ['startDateHome'], message: 'Start date must be before or on the end date.' }
  )
  .refine(
    props => {
      return Date.parse(props.lastAcceptanceDate || '') <= Date.parse(props.endDateHome || '');
    },
    {
      path: ['lastAcceptanceDate'],
      message: 'Last acceptance date cannot exceed the course end date.'
    }
  )
  .refine(
    props => {
      let total = 0;
      props.hecos?.map(hecos => {
        total += hecos.percentage;
      });
      return total === 100;
    },
    {
      path: ['hecos'],
      message: 'HECoS code proportions must sum to 100%'
    }
  );
